import {
  FC,
  InputHTMLAttributes,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
  ButtonHTMLAttributes,
  useState,
  forwardRef,
} from 'react';

interface FormFieldProps {
  label: string;
  error?: string;
}

export interface ISelectOptionProp {
  _id: string;
  name: string;
}

interface InputProps extends InputHTMLAttributes<HTMLInputElement>, FormFieldProps {}
interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement>, FormFieldProps {
  options: ISelectOptionProp[];
  showEmptyOption?: boolean;
}
interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement>, FormFieldProps {}

export const Input = forwardRef<HTMLInputElement, InputProps>(({ label, error, className = '', ...props }, ref) => {
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <input
        ref={ref}
        className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
          error ? 'border-red-300' : 'border-gray-300'
        } ${className}`}
        {...props}
      />
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
});

Input.displayName = 'Input';

interface CopyableInputProps extends Omit<InputProps, 'onChange' | 'value'> {
  value: string;
}

export const CopyableInput: FC<CopyableInputProps> = ({ label, error, className = '', value, ...props }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  return (
    <div className="mb-4 relative">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <div
        className="relative cursor-pointer group"
        onClick={handleCopy}
      >
        <input
          className={`w-full pl-3 py-2 pr-30 border rounded-md shadow-sm bg-gray-50 cursor-pointer ${
            error ? 'border-red-300' : 'border-gray-300'
          } ${className}`}
          value={value}
          readOnly
          {...props}
        />
        <div className={`absolute right-2 top-1/2 transform -translate-y-1/2 text-xs ${
          copied ? 'text-green-600' : 'text-gray-400 group-hover:text-gray-600'
        }`}>
          {copied ? 'Copied!' : 'Click to copy'}
        </div>
      </div>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
};

export const Select: FC<SelectProps> = ({
  label,
  error,
  options,
  className = '',
  showEmptyOption = true,
  ...props
}) => {
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <select
        className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
          error ? 'border-red-300' : 'border-gray-300'
        } ${className}`}
        {...props}
      >
        {showEmptyOption && <option value="">Select {label}</option>}
        {options.map((option) => (
          <option key={option._id} value={option._id}>
            {option.name}
          </option>
        ))}
      </select>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
};

export const TextArea: FC<TextAreaProps> = ({ label, error, className = '', ...props }) => {
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <textarea
        className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
          error ? 'border-red-300' : 'border-gray-300'
        } ${className}`}
        {...props}
      />
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
};

export const Button: FC<ButtonHTMLAttributes<HTMLButtonElement>> = ({
  className = '',
  ...props
}) => {
  return (
    <button
      className={`cursor-pointer px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${className}`}
      {...props}
    />
  );
};
